const SCROLL_OVERFLOW_AUTO = "auto";
const SCROLL_OVERFLOW_SCROLL = "scroll";
const MINIMUM_SCROLL_DELTA = 4;
const SCROLLABLE_OVERFLOW_VALUES = new Set([
  SCROLL_OVERFLOW_AUTO,
  SCROLL_OVERFLOW_SCROLL,
]);

interface ScrollerRect {
  height: number;
  top: number;
}

function hasScrollableOverflow(overflowY: string): boolean {
  return SCROLLABLE_OVERFLOW_VALUES.has(overflowY);
}

function isScrollContainer(element: HTMLElement): boolean {
  return hasScrollableOverflow(window.getComputedStyle(element).overflowY);
}

function canElementScroll(element: HTMLElement): boolean {
  return element.scrollHeight > element.clientHeight;
}

function getParentElement(node: HTMLElement | null): HTMLElement | null {
  if (!node) {
    return null;
  }

  return node.parentElement;
}

function isCollectableAncestor(element: HTMLElement | null): element is HTMLElement {
  if (!element) {
    return false;
  }

  return element !== document.body;
}

function getElementAncestors(node: HTMLElement | null): HTMLElement[] {
  const ancestors: HTMLElement[] = [];
  let current = getParentElement(node);

  while (isCollectableAncestor(current)) {
    ancestors.push(current);
    current = current.parentElement;
  }

  return ancestors;
}

// Find the nearest scrollable ancestor (handles nested Cscrollable / chat scroll).
// Prefer the nearest one that can take the scroll right now; when none can —
// nothing overflowing yet, or content still settling — fall back to the nearest
// scroll container rather than the document. `document.scrollingElement` is only
// the right answer when the subtree has no scroll container at all: inside the
// app shell it never scrolls, so reaching for it silently drops the scroll.
function getScrollContainer(node: HTMLElement): HTMLElement | null {
  const scrollContainers = getElementAncestors(node).filter(isScrollContainer);

  return (
    scrollContainers.find(canElementScroll) ??
    scrollContainers[0] ??
    (document.scrollingElement as HTMLElement | null)
  );
}

function isViewportScroller(scroller: HTMLElement): boolean {
  return scroller === document.scrollingElement || scroller === document.documentElement;
}

function getScrollerRect(scroller: HTMLElement): ScrollerRect {
  if (isViewportScroller(scroller)) {
    return {
      top: 0,
      height: window.innerHeight,
    };
  }

  return {
    top: scroller.getBoundingClientRect().top,
    height: scroller.clientHeight,
  };
}

function getCenteredScrollDelta({
  nodeRect,
  scrollerRect,
}: {
  nodeRect: DOMRect;
  scrollerRect: ScrollerRect;
}): number {
  const desiredCenter = scrollerRect.top + scrollerRect.height / 2;
  const currentCenter = nodeRect.top + nodeRect.height / 2;
  return currentCenter - desiredCenter;
}

function shouldScrollToCenter(delta: number): boolean {
  return Math.abs(delta) >= MINIMUM_SCROLL_DELTA;
}

// Scroll the nearest scrollable ancestor so the node's center sits in the
// viewport center. scrollIntoView({ block: 'center' }) is unreliable when the
// node is nested inside multiple scrollers and when called mid-animation.
export function scrollNodeIntoCenter(node: HTMLElement | null): void {
  if (!node) {
    return;
  }

  const scroller = getScrollContainer(node);

  if (!scroller) {
    return;
  }

  const delta = getCenteredScrollDelta({
    nodeRect: node.getBoundingClientRect(),
    scrollerRect: getScrollerRect(scroller),
  });

  if (!shouldScrollToCenter(delta)) {
    return;
  }

  scroller.scrollBy({ top: delta, behavior: "smooth" });
}
